<template>
  <q-select
    ref="select"
    :model-value="address.address_line_1"
    :options="resultsToUse"
    :placeholder="!address.address_line_1 ? 'Enter Address...' : undefined"
    :input-debounce="0"
    :disable="disable"
    :readonly="readonly"
    :loading="searchLoading"
    :hint="suggestions.length < 1 ? 'Results will appear if matches are available, otherwise enter your address manually' : undefined"
    use-input
    hide-bottom-space
    stack-label
    hide-selected
    fill-input
    hide-dropdown-icon
    clearable
    option-value="description"
    option-label="description"
    @keyup.enter="onInputEnter"
    @focus="onPopupOpen"
    @update:model-value="onResultClick"
    @filter="performFilterSearch"
    @input-value="onInputAddress"
    @clear="clearInput"
  >
    <template #option="scope">
      <q-item
        v-bind="scope.itemProps"
        :dense="!$q.platform.has.touch"
        :clickable="scope.opt.result_type !== 'header'"
        :disable="searchLoading || searching || scope.itemProps.disable"
        :active="scope.itemProps.active || (scope.opt.result_type !== 'header' && checkAddressIsSame(scope.opt, address))"
      >
        <q-item-section>
          <q-item-label :class="{'text-bold': scope.opt.result_type === 'header' || (!usingSuggestions && scope.index === 0)}">
            {{ scope.opt.description }}
          </q-item-label>
        </q-item-section>
        <q-item-section v-if="scope.opt.result_type !== 'header' && scope.opt.small_text" side>
          <q-item-label>{{ scope.opt.small_text }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>

    <template v-if="searching" #no-option>
      <q-item-wrapper
        dense
        label-class="text-bold"
        label="Loading results..."
      />
    </template>

    <template v-if="searching || usingSuggestions" #before-options>
      <q-item-wrapper class="text-bold" dense>
        <template v-if="searching">
          Loading Results...
        </template>
        <template v-else-if="usingSuggestions">
          <div class="flex q-gutter-xs justify-between items-center">
            <span>{{ suggestionsLabel }}</span>
            <q-btn
              v-if="allowRefreshSuggestions"
              size="12px"
              dense
              flat
              round
              color="blue-grey"
              icon="mdi-refresh"
              :disable="searchLoading"
              @click="$emit('refreshSuggestions')"
            />
          </div>
        </template>
      </q-item-wrapper>
    </template>

    <template v-if="!usingSuggestions && !searching" #after-options>
      <q-toolbar>
        <q-space />
        <q-btn
          v-close-popup
          size="12px"
          no-caps
          outline
          label="My address isn't listed here"
        />
      </q-toolbar>
    </template>
  </q-select>
</template>

<script>
import { getPlacePredictions, geocodePlaceId, parseAddressComponentsAsAddressModel } from 'components/partials/address/methods'
import { checkAddressIsSame } from 'components/partials/PhoneAddressEmail'
import searchMixin from 'components/partials/address/searchMixin'

/**
 * AddressSearchLine lets you search for addresses (via Google Autocomplete) or enter the "address_line_1" address manually
 */
export default {
  mixins: [searchMixin],
  props: {
    address: {
      type: Object,
      required: true
    },
    disable: {
      default: false,
      type: Boolean
    },
    readonly: {
      default: false,
      type: Boolean
    }
  },
  emits: ['clear'],
  data () {
    return {
      results: [],
      queryTimer: null,
      searching: false,
      tempHoldSearch: false
    }
  },
  computed: {
    resultsToUse () {
      return this.results.length > 0 ? this.results : this.formattedSuggestions
    },
    usingSuggestions () {
      return this.formattedSuggestions.length > 0 && this.results.length < 1
    },
    formattedSuggestions () {
      return this.suggestions.filter(item => !!item).map(item => {
        if (typeof item !== 'object') {
          return {
            result_type: 'header',
            description: item
          }
        }

        return {
          result_type: 'suggestion',
          description: item.full_address,
          small_text: item.field_name,
          ...item
        }
      })
    }
  },
  watch: {
    'resultsToUse' () {
      this.refresh()
    }
  },
  methods: {
    checkAddressIsSame,
    onPopupOpen () {
      this.$emit('loadSuggestions')
    },
    getPlacePredictions (search) {
      if (!search) { return }
      clearTimeout(this.queryTimer)
      this.searching = true
      this.queryTimer = setTimeout(async () => {
        this.results = await getPlacePredictions(search, this.autocompleteOptions)
        this.searching = false
        this.refresh()
      }, 1000) // wait 1 second before performing request
    },
    async onResultClick (item) {
      this.tempHoldSearch = true

      if (!item) {
        this.clearInput()
        this.tempHoldSearch = false
        return
      }

      if (item.result_type === 'header') {
        this.tempHoldSearch = false
        return
      }

      if (item.result_type === 'suggestion') {
        this.setModelValue(item)
      }
      else {
        const result = parseAddressComponentsAsAddressModel(await geocodePlaceId(item.place_id), this.address.address_line_1)
        this.timed(() => {
          this.setModelValue(result)
        })
      }

      this.timed(() => {
        this.tempHoldSearch = false
      }, 250)
    },
    onInputEnter () {
      if (this.$refs.select) {
        this.$refs.select.hidePopup()
      }
    },
    performFilterSearch (val, update, abort) {
      if (this.tempHoldSearch || !val) {
        if (!val) {
          this.clearResults()
        }

        update(() => {
          abort()
        })
        return
      }

      update(() => {
        this.getPlacePredictions(val)
      })
    },
    clearInput () {
      this.address.latitude = null
      this.address.longitude = null
      this.clearResults()
      this.$emit('clear')
    },
    onInputAddress (val) {
      const oldVal = (this.address.address_line_1 || '').trim().toLowerCase()
      const newVal = (val || '').trim().toLowerCase()

      this.address.address_line_1 = val

      // Remove lat/lng geocode if user has changed address
      if (oldVal !== newVal && (this.address.latitude || this.address.longitude)) {
        this.address.latitude = null
        this.address.longitude = null
      }
    },
    setModelValue (newAddressObj) {
      if (!newAddressObj) {
        return
      }

      this.$nextTick(() => {
        const isSameAddress = checkAddressIsSame(this.address, newAddressObj)
        const isGeocodable = !!newAddressObj.latitude && !!newAddressObj.longitude

        this.address.address_line_1 = newAddressObj.address_line_1
        this.address.locality = newAddressObj.locality
        this.address.postal_code = newAddressObj.postal_code
        this.address.state = newAddressObj.state
        this.address.country = newAddressObj.country

        if (!isSameAddress || isGeocodable) {
          this.address.latitude = isGeocodable ? newAddressObj.latitude : null
          this.address.longitude = isGeocodable ? newAddressObj.longitude : null
        }
      })
    },
    clearResults () {
      this.searching = false
      clearTimeout(this.queryTimer)
      this.results = []
    },
    refresh () {
      if (!this.$refs.select) {
        return
      }
      this.$refs.select.refresh()
    },
    focus () {
      if (this.$refs.select) {
        this.$refs.select.showPopup()
      }
    }
  },
  created () {
    this.$gmapApiPromiseLazy()
  }
}
</script>
